import { mat4 } from 'gl-matrix';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { ImageSlicePositions } from '~/containers/viewer/viewport/Viewport';
import { Modality } from '~/domain/dicom/modality';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import { Image3D } from '~/domain/patient/image3d';
import { RigidRegistration } from '~/domain/patient/rigidRegistration';
import { StructureSet } from '~/domain/patient/structureset';
import { createStructureSetReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { getRegistrationStore } from '~/domain/ServiceContainer';
import { ImportResult } from '~/domain/services/dicomProcessor';
import { ImageViewMode } from '~/engine/core/render/renderers/ImageRenderer';
import { EnvironmentObjects } from '~/engine/data/EnvironmentObjects';
import { Orientations, PlaneViewType } from '~/engine/view/views/PlaneView';
import { currentDoseState } from '~/state/atoms/dose/doseAtoms';
import { optimizationObjectivesState } from '~/state/atoms/dose/optimizationObjectivesAtom';
import {
    environmentState,
    lowerSideViewState,
    mainViewState,
    upperSideViewState,
} from '~/state/atoms/engineAtoms';
import {
    checkerboardDensityState,
    FusionColorMapping,
    grayscaleFusionColorMapping,
    imageSecondaryAmountState,
    primaryImageState,
    secondaryImageState,
    selectedFusionColorMappingState,
    selectedSecondaryImageModeState,
} from '~/state/atoms/image/imageAtoms';
import {
    PETColorMap,
    selectedPETColorMappingState,
} from '~/state/atoms/image/petImageAtom';
import {
    patientRepositoryState,
    selectedPatientIdState,
} from '~/state/atoms/patientAtoms';
import {
    multiSelectedRoiNumbersState,
    selectedROIState,
} from '~/state/atoms/structureSet/roiAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import {
    selectedToolState,
    voiBoundsState,
    voiCheckboxState,
} from '~/state/atoms/toolsAtoms';
import { handleError } from '~/utils/errors';
import {
    calculateNormalizedOrientationValue,
    getDominantAxisSign,
} from '~/utils/units';
import useViewportSecondaryEnvironment from './useViewportSecondaryEnvironment';

/**
 * Picks a sensible default primary image when an import produces
 * more than one candidate. Preference order:
 *   1. CT modality over anything else (non-PT candidates only arrive
 *      here; CT beats MR etc.)
 *   2. Newest series date (missing dates rank lowest)
 *   3. Largest slice count (thickest stack wins ties)
 *
 * Assumes `candidates` is non-empty.
 */
function pickDefaultPrimaryImage(candidates: Image3D[]): Image3D {
    const score = (img: Image3D) => {
        const isCt = img.firstSlice.modality === Modality.CT ? 1 : 0;
        const dateMs = img.seriesMetadata.seriesDate?.getTime() ?? 0;
        const sliceCount = img.length;
        return { isCt, dateMs, sliceCount };
    };
    return [...candidates].sort((a, b) => {
        const sa = score(a);
        const sb = score(b);
        if (sa.isCt !== sb.isCt) {
            return sb.isCt - sa.isCt;
        }
        if (sa.dateMs !== sb.dateMs) {
            return sb.dateMs - sa.dateMs;
        }
        return sb.sliceCount - sa.sliceCount;
    })[0];
}

export default function useViewportEnvironment() {
    const environment = useRecoilValue(environmentState);
    const mainView = useRecoilValue(mainViewState);
    const upperSideView = useRecoilValue(upperSideViewState);
    const lowerSideView = useRecoilValue(lowerSideViewState);
    const patientRepository = useRecoilValue(patientRepositoryState);
    const selectedPatientId = useRecoilValue(selectedPatientIdState);
    const setSelectedPatientId = useSetRecoilState(selectedPatientIdState);
    const setPrimaryImage = useSetRecoilState(primaryImageState);
    const setSecondaryImage = useSetRecoilState(secondaryImageState);
    const setCurrentStructureSet = useSetRecoilState(currentStructureSetState);
    const setCurrentDose = useSetRecoilState(currentDoseState);
    const setOptimizationObjectives = useSetRecoilState(
        optimizationObjectivesState
    );
    const registrationStore = getRegistrationStore();
    const setSelectedROI = useSetRecoilState<number>(selectedROIState);
    const setMultiSelectedRois = useSetRecoilState(
        multiSelectedRoiNumbersState
    );
    const setTool = useSetRecoilState(selectedToolState);
    const setSelectedSecondaryImageMode = useSetRecoilState(
        selectedSecondaryImageModeState
    );
    const setSelectedFusionColorMapping = useSetRecoilState(
        selectedFusionColorMappingState
    );
    const setSelectedPETColorMapping = useSetRecoilState(
        selectedPETColorMappingState
    );
    const setCheckerboardDensity = useSetRecoilState(checkerboardDensityState);
    const setImageSecondaryAmount = useSetRecoilState(
        imageSecondaryAmountState
    );
    const setVoiBounds = useSetRecoilState(voiBoundsState);
    const setVoiCheckbox = useSetRecoilState(voiCheckboxState);
    const { removeEnvironmentSecondaryImageSideBySide } =
        useViewportSecondaryEnvironment();

    const searchPatient = (patientId?: string) => {
        if (patientId == null) {
            return;
        }
        const patientToOpen = patientRepository
            .getAll()
            .find((patient) => patient.dataSet.PatientID === patientId);
        return patientToOpen;
    };

    const setStructureSet = (structureSet: StructureSet | undefined) => {
        if (structureSet) {
            setCurrentStructureSet(createStructureSetReadModel(structureSet));
        } else {
            setCurrentStructureSet(undefined);
        }
    };

    const setEnvironmentObjects = (environmentObjects?: EnvironmentObjects) => {
        setPrimaryImage(environmentObjects?.primaryImage?.parent);
        setStructureSet(environmentObjects?.structureSet?.parent);
        setSecondaryImage(environmentObjects?.secondaryImage?.parent);
        setCurrentDose(environmentObjects?.dose?.parent);
        registrationStore.selectRegistration(
            environmentObjects?.registration?.sopInstanceUid,
            environmentObjects?.secondaryImage?.parent?.seriesUid
        );
    };

    const setEnvironmentDose = (doses: DoseMatrix[], patientId: string) => {
        const patient = patientRepository.getByPatientId(patientId);
        const dose = doses.find((dose) =>
            patient?.getDoseWithSopInstanceUid(dose.sopInstanceUid)
        );
        if (environment && dose) {
            setOptimizationObjectives(dose.optimizationObjectives ?? []);
            setSelectedPatientId(patientId);
            const environmentObjects = environment.setDose(dose);
            setEnvironmentObjects(environmentObjects);
        }
    };

    const unloadDose = () => {
        if (environment) {
            environment.setDose(undefined);
            setCurrentDose(undefined);
            setOptimizationObjectives([]);
        }
    };

    const setEnvironmentStructureSet = (
        structureSets: StructureSet[],
        patientId: string
    ) => {
        const patient = patientRepository.getByPatientId(patientId);
        const structureSet = structureSets.find((struct) =>
            patient?.getStructureSetWithSopInstanceUid(struct.dicomUid)
        );
        if (environment && structureSet) {
            const environmentObjects =
                environment.setStructureSet(structureSet);
            if (environmentObjects) {
                cleanupStructureSetContext();
                setSelectedPatientId(patientId);
                updateSelectedFusionColorMapping(grayscaleFusionColorMapping);
                setEnvironmentObjects(environmentObjects);
            }
        }
    };

    const unloadStructureSet = () => {
        if (environment) {
            environment.setStructureSet(undefined);
            cleanupStructureSetContext();
        }
    };

    const cleanupStructureSetContext = () => {
        setStructureSet(undefined);
        setSelectedROI(-1);
        setMultiSelectedRois(new Set());
        setTool('pan');
        unloadDose();
    };

    const setEnvironmentPrimaryImage = (
        images: Image3D[],
        patientId: string
    ) => {
        const patient = patientRepository.getByPatientId(patientId);
        const candidates = images.filter((img) =>
            patient?.getImage3DWithDicomUid(img.dicomUid)
        );
        if (candidates.length === 0) {
            return;
        }
        // Auto-select a sensible default so tabs that depend on a
        // primary image light up immediately after import. Prefer CT
        // over other modalities, then the newest series, then the
        // one with the most slices. The user can still switch later
        // via the image list.
        const image = pickDefaultPrimaryImage(candidates);
        if (environment && image) {
            setSelectedPatientId(patientId);
            removeEnvironmentSecondaryImageSideBySide();
            const environmentObjects = environment.setImage(image);
            setEnvironmentObjects(environmentObjects);
            updateSelectedFusionColorMapping(grayscaleFusionColorMapping);
            updatePrimaryImageAlpha(100);
            setTool('pan');
        }
    };

    const removePrimaryImage = () => {
        if (environment) {
            environment.setImage(undefined);
            setPrimaryImage(undefined);
            removeEnvironmentSecondaryImage();
            removeEnvironmentSecondaryImageSideBySide();
            cleanupStructureSetContext();
        }
    };

    const setEnvironmentSecondaryImage = (
        image: Image3D,
        registration?: RigidRegistration
    ) => {
        if (environment && image) {
            const environmentObjects = environment.setSecondaryImage(
                image,
                registration
            );
            // `environment.setSecondaryImage` returns `undefined` when
            // it rejects the secondary image — e.g. no primary is
            // loaded, FORs don't match and no compatible registration
            // is available. Passing that `undefined` to
            // `setEnvironmentObjects` would silently wipe the primary
            // image / structure set / dose atoms via optional
            // chaining, dumping the user back to the empty-state
            // sidebar. Bail out instead and leave the viewport as-is.
            if (!environmentObjects) {
                return;
            }
            environment?.setRegistration(registration);
            setEnvironmentObjects(environmentObjects);
            setSecondaryImage(image);
            setVoiCheckbox(false);
            setVoiBounds(null);
            updatePrimaryImageAlpha(50);
        }
    };

    const removeEnvironmentSecondaryImage = () => {
        if (environment) {
            environment.windowLevelMode = PlaneViewType.Primary;
            updateImageViewMode(ImageViewMode.AlphaBlending);
            updateSelectedFusionColorMapping(grayscaleFusionColorMapping);
            environment.setSecondaryImage(undefined);
            environment.setRegistration(undefined);
            setSecondaryImage(undefined);
            registrationStore.selectRegistration(undefined);
            updatePrimaryImageAlpha(100);
            setTool('pan');
        }
    };

    const setEnvironmentRegistration = (
        registrations: RigidRegistration[] | undefined,
        patientId: string
    ) => {
        if (!registrations || registrations.length === 0) {
            return;
        }
        for (const reg of registrations) {
            if (environment && environment.secondaryImage && reg) {
                setSelectedPatientId(patientId);
                setEnvironmentSecondaryImage(
                    environment.secondaryImage.parent,
                    reg
                );
            }
        }
        setTool('pan');
    };

    const updateEnvironmentRegistrationTransform = (newTransform: mat4) => {
        if (environment) {
            environment.setRegistrationMatrix(newTransform);
            environment.registration?.syncTransformationToDataset();
            setVoiCheckbox(false);
            setVoiBounds(null);
        }
    };

    const updateImageViewMode = (mode: ImageViewMode) => {
        if (environment) {
            setSelectedSecondaryImageMode(mode);
            environment.updateImageViewMode(mode);
        }
    };

    const updateCheckerboardDensity = (density: number) => {
        if (environment) {
            setCheckerboardDensity(density);
            environment.updateCheckerboardDensity(density);
        }
    };

    const updatePrimaryImageAlpha = (alpha: number) => {
        if (environment) {
            setImageSecondaryAmount(alpha);
            environment.updatePrimaryImageAlpha(alpha / 100);
        }
    };

    const updateSelectedFusionColorMapping = (preset: FusionColorMapping) => {
        if (environment) {
            setSelectedFusionColorMapping(preset);
            environment.changeFusionColorMapping(
                preset.primaryValues,
                preset.secondaryValues
            );
        }
    };

    const updateSelectedPETFusionColorMapping = (preset: PETColorMap) => {
        if (environment) {
            setSelectedPETColorMapping(preset);
            environment.changePetFusionColorMapping(preset.id);
        }
    };

    const resolvePatient = (importResult: ImportResult) => {
        try {
            let patient;
            if (importResult.images[0]) {
                const sliceData = importResult.images[0]?.firstSlice;
                patient = searchPatient(sliceData.datasetWrapper.PatientID);
            } else if (importResult.structureSets[0]) {
                patient = searchPatient(
                    importResult.structureSets[0].dicomStructureSet
                        .datasetWrapper.PatientID
                );
            } else if (importResult.doses[0]) {
                patient = searchPatient(
                    importResult.doses[0].structureSet?.dicomStructureSet
                        ?.datasetWrapper.PatientID
                );
            }

            return patient ? patient.dataSet.PatientID : selectedPatientId;
        } catch (error) {
            handleError(
                error,
                'Error when trying to resolve patient data from imported object.'
            );
        }
    };

    const initializeEnvironment = async (importResult: ImportResult) => {
        const { images, doses, structureSets, patients, registrations } =
            importResult;
        const patientId = patients[0]
            ? importResult.patients[0].PatientID
            : resolvePatient(importResult);
        if (patientId != null) {
            // intentional != to catch both null and undefined
            setSelectedPatientId(patientId);
            if (images.length > 0) {
                await setEnvironmentPrimaryImage(images, patientId);
            }
            if (structureSets.length > 0) {
                await setEnvironmentStructureSet(structureSets, patientId);
            }
            if (doses.length > 0) {
                await setEnvironmentDose(doses, patientId);
            }
            if (registrations.length > 0) {
                await setEnvironmentRegistration(registrations, patientId);
            }
        }
    };

    const getEnvImagePositions = (
        currentImageSliceOrientationPositions: ImageSlicePositions
    ): ImageSlicePositions => {
        if (
            !environment ||
            !mainView ||
            !upperSideView ||
            !lowerSideView ||
            !environment.image
        ) {
            return currentImageSliceOrientationPositions;
        }

        const views = [mainView, upperSideView, lowerSideView];
        const updated: ImageSlicePositions = {
            ...currentImageSliceOrientationPositions,
        };

        for (const orientation of Object.values(Orientations)) {
            const view = views.find((v) => v.label === orientation);
            if (view?.planeCoordinates === undefined) {
                return currentImageSliceOrientationPositions;
            }

            const viewDirSign = getDominantAxisSign(view.viewDirection);
            updated[orientation] = calculateNormalizedOrientationValue(
                view.planeCoordinates,
                viewDirSign
            );
        }
        return updated;
    };

    const environmentObjectIds = environment?.getEnvironmentObjectIds;

    return {
        environment,
        environmentObjectIds,
        initializeEnvironment,
        setEnvironmentDose,
        unloadDose,
        setEnvironmentStructureSet,
        unloadStructureSet,
        setEnvironmentPrimaryImage,
        removePrimaryImage,
        setEnvironmentSecondaryImage,
        removeEnvironmentSecondaryImage,
        getEnvImagePositions,
        updateImageViewMode,
        updateCheckerboardDensity,
        updatePrimaryImageAlpha,
        updateSelectedFusionColorMapping,
        updateSelectedPETFusionColorMapping,
        updateEnvironmentRegistrationTransform,
    };
}
